import { useEffect, useMemo, useState } from "react";
import { evaluateDiscount } from "@/lib/discountWindow";
import type { Discount } from "@/types/restaurant";

// The window arithmetic lives in `@/lib/discountWindow` (pure, React-free) so
// the Bun server's public agent API can share the exact same definition of
// "live now". Re-exported here because every existing call site imports these
// two from this module.
export { isDiscountLive, isOneTimeEnded } from "@/lib/discountWindow";

/**
 * Picks the discount a venue should surface on the consumer card *right now*.
 *
 * Replaces the old per-restaurant dead-hour evaluation. Each venue publishes
 * any number of free-form `discounts` rows (info text + HH:MM window + optional
 * days). This hook ticks once a second, walks the venue's active discounts in
 * operator order, and returns the first one whose window currently contains
 * `now` — together with a live countdown to its end.
 */

export interface UseActiveDiscountOptions {
  /** Disable evaluation (e.g. when the venue is closed). */
  disabled?: boolean;
  /** Override clock for tests/Storybook. Defaults to device time. */
  now?: () => Date;
  /** Tick rate for the countdown (ms). Default 1000. */
  tickMs?: number;
}

export interface ActiveDiscountResult {
  /** The currently-active discount, or null when none is in-window. */
  discount: Discount | null;
  /** "M:SS" or "H:MM:SS" while active, null otherwise. */
  countdown: string | null;
  /** ISO timestamp the active discount expires, or null. */
  expiresAt: string | null;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return "0:00";
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function useActiveDiscount(
  discounts: Discount[] | undefined,
  options: UseActiveDiscountOptions = {},
): ActiveDiscountResult {
  const tickMs = options.tickMs ?? 1000;
  const getNow = options.now ?? (() => new Date());
  const disabled = options.disabled ?? false;
  const [now, setNow] = useState<Date>(() => getNow());

  useEffect(() => {
    if (disabled) return;
    const id = setInterval(() => setNow(getNow()), tickMs);
    return () => clearInterval(id);
  }, [tickMs, getNow, disabled]);

  return useMemo(() => {
    const none: ActiveDiscountResult = { discount: null, countdown: null, expiresAt: null };
    if (disabled || !discounts || discounts.length === 0) return none;
    for (const d of discounts) {
      const res = evaluateDiscount(d, now);
      if (res) {
        return {
          discount: d,
          countdown: formatCountdown(res.msRemaining),
          expiresAt: res.expiresAt.toISOString(),
        };
      }
    }
    return none;
  }, [discounts, now, disabled]);
}
